import { useState } from "react";
import { useTheme } from "../lib/themeContext";
import { useAuth } from "../lib/authContext";
import { Palette, Globe, Bell, User, Plug, Check, Sun, Moon, Sparkles, Heart } from "lucide-react";

const TABS = [
  { id: "appearance", label: "Apparence", icon: Palette },
  { id: "language", label: "Langue", icon: Globe },
  { id: "notifications", label: "Notifications", icon: Bell },
  { id: "account", label: "Compte & Workspace", icon: User },
  { id: "integrations", label: "Intégrations", icon: Plug },
];

const THEME_PREVIEWS = {
  light:  { name: "Clair",   icon: Sun,       bg: "#FFFFFF", side: "#1E1B4B", accent: "#7C3AED" },
  dark:   { name: "Sombre",  icon: Moon,      bg: "#0A0A0F", side: "#0F0E1F", accent: "#A78BFA" },
  purple: { name: "Violet",  icon: Sparkles,  bg: "#F3F0FF", side: "#2E1065", accent: "#A78BFA" },
  pink:   { name: "Rose",    icon: Heart,     bg: "#FFF1F7", side: "#831843", accent: "#EC4899" },
};

const ACCENT_OPTIONS = [
  { id: "purple", color: "#7C3AED", label: "Violet" },
  { id: "pink",   color: "#EC4899", label: "Rose" },
  { id: "blue",   color: "#3B82F6", label: "Bleu" },
  { id: "green",  color: "#10B981", label: "Vert" },
  { id: "orange", color: "#F59E0B", label: "Orange" },
  { id: "red",    color: "#EF4444", label: "Rouge" },
];

export default function Settings() {
  const [tab, setTab] = useState("appearance");
  return (
    <div className="space-y-6 animate-fadeUp" data-testid="settings-page">
      <header>
        <div className="text-[10px] font-semibold uppercase tracking-[0.14em]" style={{ color: "var(--brand-purple)" }}>CDO Agency</div>
        <h1 className="font-display text-4xl font-bold tracking-tight" style={{ color: "var(--text-primary)" }}>Paramètres</h1>
        <p className="text-sm mt-1.5" style={{ color: "var(--text-muted)" }}>Personnalise ton workspace.</p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-6">
        <nav className="space-y-1">
          {TABS.map(t => {
            const I = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`settings-tab-${t.id}`}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition text-left"
                style={{
                  background: tab === t.id ? "var(--brand-purple)" : "transparent",
                  color: tab === t.id ? "#FFFFFF" : "var(--text-secondary)",
                }}>
                <I className="w-4 h-4" strokeWidth={1.6} /> {t.label}
              </button>
            );
          })}
        </nav>
        <div className="surface p-7 min-h-[400px]">
          {tab === "appearance" && <AppearanceTab />}
          {tab === "language" && <LanguageTab />}
          {tab === "notifications" && <NotificationsTab />}
          {tab === "account" && <AccountTab />}
          {tab === "integrations" && <IntegrationsTab />}
        </div>
      </div>
    </div>
  );
}

function AppearanceTab() {
  const t = useTheme();
  return (
    <div className="space-y-7" data-testid="appearance-tab">
      <Section title="Thème">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(THEME_PREVIEWS).map(([k, p]) => {
            const I = p.icon;
            const active = t.theme === k;
            return (
              <button key={k} onClick={() => t.set({ theme: k })} data-testid={`theme-${k}`}
                className="relative rounded-xl overflow-hidden border-2 transition"
                style={{ borderColor: active ? "var(--brand-purple)" : "var(--border-default)" }}>
                <div className="aspect-[4/3] flex" style={{ background: p.bg }}>
                  <div className="w-1/3" style={{ background: p.side }}></div>
                  <div className="flex-1 p-2 flex items-center justify-center">
                    <I className="w-4 h-4" style={{ color: p.accent }} />
                  </div>
                </div>
                <div className="px-3 py-2 text-xs font-semibold" style={{ background: "var(--bg-surface)", color: "var(--text-primary)" }}>
                  {p.name} {active && <Check className="inline w-3 h-3 ml-1" style={{ color: "var(--brand-purple)" }} />}
                </div>
              </button>
            );
          })}
        </div>
      </Section>

      <Section title="Couleur d'accent">
        <div className="flex flex-wrap gap-3">
          {ACCENT_OPTIONS.map(o => {
            const active = t.accent === o.id;
            return (
              <button key={o.id} onClick={() => t.set({ accent: o.id })} data-testid={`accent-${o.id}`}
                className="flex items-center gap-2 px-3 py-2 rounded-lg border transition"
                style={{
                  borderColor: active ? o.color : "var(--border-default)",
                  background: active ? o.color + "15" : "transparent",
                }}>
                <span className="w-4 h-4 rounded-full" style={{ background: o.color }}></span>
                <span className="text-xs font-semibold" style={{ color: active ? o.color : "var(--text-secondary)" }}>{o.label}</span>
                {active && <Check className="w-3 h-3" style={{ color: o.color }} />}
              </button>
            );
          })}
        </div>
      </Section>

      <Section title="Taille de la sidebar">
        <div className="flex gap-2">
          {[["compact", "Compacte"], ["normal", "Normale"], ["large", "Large"]].map(([id, label]) => (
            <button key={id} onClick={() => t.set({ sidebarSize: id })} data-testid={`size-${id}`}
              className="px-4 py-2 rounded-lg text-xs font-semibold transition"
              style={{
                background: t.sidebarSize === id ? "var(--brand-purple)" : "var(--bg-base)",
                color: t.sidebarSize === id ? "white" : "var(--text-secondary)",
                border: `1px solid ${t.sidebarSize === id ? "var(--brand-purple)" : "var(--border-default)"}`,
              }}>
              {label}
            </button>
          ))}
        </div>
      </Section>

      <Section title="Densité d'affichage">
        <div className="flex gap-2">
          {[["comfortable", "Confortable"], ["compact", "Compact"]].map(([id, label]) => (
            <button key={id} onClick={() => t.set({ density: id })} data-testid={`density-${id}`}
              className="px-4 py-2 rounded-lg text-xs font-semibold transition"
              style={{
                background: t.density === id ? "var(--brand-purple)" : "var(--bg-base)",
                color: t.density === id ? "white" : "var(--text-secondary)",
                border: `1px solid ${t.density === id ? "var(--brand-purple)" : "var(--border-default)"}`,
              }}>
              {label}
            </button>
          ))}
        </div>
      </Section>
    </div>
  );
}

function LanguageTab() {
  const t = useTheme();
  return (
    <div className="space-y-5" data-testid="language-tab">
      <Section title="Langue de l'interface">
        <div className="space-y-2">
          {[
            { id: "fr", flag: "🇫🇷", label: "Français" },
            { id: "en", flag: "🇬🇧", label: "English" },
            { id: "es", flag: "🇪🇸", label: "Español" },
          ].map(l => (
            <button key={l.id} onClick={() => t.set({ lang: l.id })} data-testid={`lang-${l.id}`}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border transition text-left"
              style={{
                borderColor: t.lang === l.id ? "var(--brand-purple)" : "var(--border-default)",
                background: t.lang === l.id ? "var(--brand-purple)10" : "transparent",
              }}>
              <span className="text-2xl">{l.flag}</span>
              <span className="flex-1 font-semibold text-sm" style={{ color: "var(--text-primary)" }}>{l.label}</span>
              {t.lang === l.id && <Check className="w-4 h-4" style={{ color: "var(--brand-purple)" }} />}
            </button>
          ))}
        </div>
        <p className="text-xs mt-3" style={{ color: "var(--text-muted)" }}>Le changement est instantané, sans rechargement.</p>
      </Section>
    </div>
  );
}

function NotificationsTab() {
  const [s, setS] = useState({ email: true, push: false, freq: "weekly" });
  return (
    <div className="space-y-5" data-testid="notifications-tab">
      <Section title="Notifications">
        <ToggleRow label="Alertes par email" checked={s.email} on={v => setS({ ...s, email: v })} />
        <ToggleRow label="Notifications push" checked={s.push} on={v => setS({ ...s, push: v })} />
      </Section>
      <Section title="Fréquence des rapports">
        <div className="flex gap-2">
          {[["daily", "Quotidien"], ["weekly", "Hebdomadaire"], ["monthly", "Mensuel"]].map(([k, l]) => (
            <button key={k} onClick={() => setS({ ...s, freq: k })}
              className="px-4 py-2 rounded-lg text-xs font-semibold"
              style={{
                background: s.freq === k ? "var(--brand-purple)" : "var(--bg-base)",
                color: s.freq === k ? "white" : "var(--text-secondary)",
                border: `1px solid ${s.freq === k ? "var(--brand-purple)" : "var(--border-default)"}`,
              }}>{l}</button>
          ))}
        </div>
      </Section>
      <div className="text-xs italic" style={{ color: "var(--text-muted)" }}>
        ℹ️ Les notifications email/push seront activées dans une prochaine version (Resend + service worker).
      </div>
    </div>
  );
}

function AccountTab() {
  const { user } = useAuth();
  return (
    <div className="space-y-5" data-testid="account-tab">
      <Section title="Profil">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full cdo-gradient flex items-center justify-center text-white font-bold text-xl">
            {(user?.name || "?").slice(0, 2).toUpperCase()}
          </div>
          <div>
            <div className="font-display text-lg font-bold" style={{ color: "var(--text-primary)" }}>{user?.name}</div>
            <div className="text-xs font-mono" style={{ color: "var(--text-muted)" }}>{user?.email}</div>
            <span className="inline-block mt-1 text-[10px] font-semibold uppercase px-2 py-0.5 rounded-md" style={{ background: "var(--brand-purple)15", color: "var(--brand-purple)" }}>
              {user?.role}{user?.is_owner ? " · OWNER" : ""}
            </span>
          </div>
        </div>
      </Section>
      <Section title="Workspace">
        <div className="text-sm" style={{ color: "var(--text-secondary)" }}>
          CDO Agency — Social Media OS
        </div>
      </Section>
      <Section title="Plan actuel">
        <div className="surface p-4">
          <div className="font-semibold" style={{ color: "var(--text-primary)" }}>Production (déployé)</div>
          <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>50 crédits Emergent / mois · accès complet</div>
        </div>
      </Section>
    </div>
  );
}

function IntegrationsTab() {
  const integrations = [
    { id: "instagram", label: "Instagram", icon: "📷", status: "pending", note: "OAuth en attente clés Meta" },
    { id: "tiktok", label: "TikTok", icon: "🎵", status: "pending", note: "OAuth en attente clés TikTok" },
    { id: "youtube", label: "YouTube", icon: "▶️", status: "soon", note: "Bientôt disponible" },
    { id: "linkedin", label: "LinkedIn", icon: "💼", status: "soon", note: "Bientôt disponible" },
    { id: "pinterest", label: "Pinterest", icon: "📌", status: "soon", note: "Bientôt disponible" },
  ];
  return (
    <div className="space-y-5" data-testid="integrations-tab">
      <Section title="Plateformes sociales">
        <div className="space-y-2">
          {integrations.map(i => (
            <div key={i.id} className="flex items-center justify-between p-4 rounded-xl border" style={{ borderColor: "var(--border-default)", background: "var(--bg-base)" }}>
              <div className="flex items-center gap-3">
                <span className="text-2xl">{i.icon}</span>
                <div>
                  <div className="font-semibold text-sm" style={{ color: "var(--text-primary)" }}>{i.label}</div>
                  <div className="text-xs" style={{ color: "var(--text-muted)" }}>{i.note}</div>
                </div>
              </div>
              <span className="text-[10px] font-semibold uppercase px-2 py-1 rounded-md" style={{
                background: i.status === "connected" ? "#10B98120" : i.status === "pending" ? "#F59E0B20" : "#9CA3AF20",
                color: i.status === "connected" ? "#10B981" : i.status === "pending" ? "#F59E0B" : "#9CA3AF",
              }}>
                {i.status === "connected" ? "Connecté" : i.status === "pending" ? "En attente" : "Bientôt"}
              </span>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div>
    <div className="text-[10px] font-semibold uppercase tracking-[0.14em] mb-3" style={{ color: "var(--text-muted)" }}>{title}</div>
    <div className="space-y-2">{children}</div>
  </div>
);

const ToggleRow = ({ label, checked, on }) => (
  <label className="flex items-center justify-between p-3 rounded-lg cursor-pointer" style={{ background: "var(--bg-base)" }}>
    <span className="text-sm" style={{ color: "var(--text-primary)" }}>{label}</span>
    <button type="button" onClick={() => on(!checked)} role="switch" aria-checked={checked}
      className="w-10 h-6 rounded-full relative transition"
      style={{ background: checked ? "var(--brand-purple)" : "#D1D5DB" }}>
      <span className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition"
        style={{ transform: checked ? "translateX(16px)" : "translateX(0)" }}></span>
    </button>
  </label>
);
